import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Parent } from '@/types/models';
import { toast } from 'sonner';
import { mapToInviteStatus } from '@/utils/typeUtils';
import { mapToParent } from '@/utils/supabaseTypeMappers';
import { EmailService } from '@/services/emailService';
import { useAuth } from '@/hooks/useAuth';
import { FamilyService } from '@/services/familyService';
import { fetchOrganizationData } from '@/utils/auth/organizationData';

export const useParentActions = () => {
  const [editingParent, setEditingParent] = useState<Parent | null>(null);
  const [isEditParentDialogOpen, setIsEditParentDialogOpen] = useState(false);
  const [isDeletingParent, setIsDeletingParent] = useState(false);
  const { user } = useAuth();

  const handleSendInvite = async (parentId: string, allParents: Parent[], setAllParents: React.Dispatch<React.SetStateAction<Parent[]>>) => {
    const loadingToastId = toast.loading("Sending invite...");
    
    try {
      // First get the parent details to get their email and family info
      const { data: parent, error: parentError } = await supabase
        .from('parents')
        .select('*, families(name)')
        .eq('id', parentId)
        .single();

      if (parentError) throw parentError;
      
      if (!parent || !parent.email) {
        toast.dismiss(loadingToastId);
        toast.error("Cannot send invite: No email address found for parent");
        return;
      }

      // Update the parent status to sent
      const { data: updatedParent, error: updateError } = await supabase
        .from('parents')
        .update({ invite_status: 'sent' })
        .eq('id', parentId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Fetch organization name
      let organizationName = undefined;
      try {
        const orgData = await fetchOrganizationData(parent.organization_id);
        organizationName = orgData?.name;
      } catch (orgErr) {
        console.warn('Could not fetch organization name for invite:', orgErr);
      }

      // Send the invitation email
      const metadata = {
        family_id: parent.family_id,
        familyName: parent.families?.name || 'Family account',
        parentId: parentId,
        role: 'parent',
        organization_id: parent.organization_id,
        organizationName,
        firstName: parent.first_name,
        lastName: parent.last_name
      };

      const { success, error: emailError } = await EmailService.sendInvite(
        parent.email,
        metadata
      );

      if (emailError) {
        console.error('Email sending failed:', emailError);
        // We don't throw here as we've already updated the status
      }

      const parentToUpdate = allParents.find(p => p.id === parentId);
      
      if (updatedParent && parentToUpdate) {
        const typedParent = mapToParent({
          ...updatedParent,
          email: parentToUpdate.email || ''
        });
        
        setAllParents(prev => prev.map(p => p.id === parentId ? typedParent : p));

        toast.dismiss(loadingToastId);
        toast.success("Invite sent successfully", {
          description: `An invite has been sent to ${parentToUpdate.email}`
        });
      }
    } catch (error) {
      toast.dismiss(loadingToastId);
      console.error("Failed to send invite:", error);
      toast.error("Failed to send invite", {
        description: "There was an error sending the invite. Please try again."
      });
    }
  };

  const handleEditParent = (parent: Parent) => {
    setEditingParent(parent);
    setIsEditParentDialogOpen(true);
  };

  const handleParentUpdated = (
    parentId: string,
    updatedData: Partial<Parent>,
    allParents: Parent[],
    setAllParents: React.Dispatch<React.SetStateAction<Parent[]>>
  ) => {
    return new Promise<Parent | null>((resolve, reject) => {
      // Call the API to update the parent
      FamilyService.updateParent(parentId, updatedData)
        .then(updatedParent => {
          if (updatedParent) {
            // If primary status was changed, refetch all parents to ensure consistency
            if (updatedData.is_primary !== undefined) {
              // Get the family_id to refetch all parents for this family
              const familyId = allParents.find(p => p.id === parentId)?.family_id;
              if (familyId) {
                FamilyService.getParentsByFamily(familyId)
                  .then(refreshedParents => {
                    setAllParents(refreshedParents);
                  })
                  .catch(error => {
                    console.error('Error refreshing parents:', error);
                    // Fallback to manual update if refresh fails
                    const updatedParents = allParents.map(parent => {
                      if (parent.id === parentId) {
                        return { ...parent, ...updatedParent };
                      } else if (updatedData.is_primary === true) {
                        // Remove primary status from other parents
                        return { ...parent, is_primary: false };
                      }
                      return parent;
                    });
                    setAllParents(updatedParents);
                  });
              }
            } else {
              // For non-primary updates, just update the single parent
              const updatedParents = allParents.map(parent => 
                parent.id === parentId ? { ...parent, ...updatedParent } : parent
              );
              setAllParents(updatedParents);
            }
            
            // Show success message
            toast.success(`${updatedParent.first_name} ${updatedParent.last_name} updated successfully`);
            
            // Close the dialog
            setIsEditParentDialogOpen(false);
            setEditingParent(null);
            
            // Resolve with the updated parent
            resolve(updatedParent);
          } else {
            reject(new Error('Failed to update parent'));
          }
        })
        .catch(error => {
          console.error('Error updating parent:', error);
          toast.error('Failed to update parent');
          reject(error);
        });
    });
  };

  const handleDeleteParent = async (
    parentId: string, 
    allParents: Parent[], 
    setAllParents: React.Dispatch<React.SetStateAction<Parent[]>>
  ) => {
    const loadingToastId = toast.loading("Deleting parent...");
    
    try {
      setIsDeletingParent(true);
      
      // Call the soft delete service instead of permanent delete
      const success = await FamilyService.softDeleteParent(parentId);
      
      if (success) {
        // If successful, update the UI by removing the parent from the list
        const updatedParents = allParents.filter(p => p.id !== parentId);
        setAllParents(updatedParents);
        toast.dismiss(loadingToastId);
        toast.success("Parent removed successfully");
      } else {
        toast.dismiss(loadingToastId);
        toast.error("Failed to remove parent");
      }
    } catch (error) {
      console.error("Error deleting parent:", error);
      toast.dismiss(loadingToastId);
      toast.error("Failed to remove parent");
    } finally {
      setIsDeletingParent(false);
    }
  };

  const handleToggleParentActiveStatus = async (
    parent: Parent,
    isActive: boolean,
    allParents: Parent[],
    setAllParents: React.Dispatch<React.SetStateAction<Parent[]>>
  ) => {
    const loadingToastId = toast.loading(`${isActive ? 'Activating' : 'Deactivating'} parent...`);
    
    try {
      const updatedParent = await FamilyService.toggleParentActiveStatus(parent.id, isActive);
      
      if (updatedParent) {
        // Update the parents array with the updated parent
        const updatedParents = allParents.map(p => 
          p.id === parent.id ? { ...p, is_active: isActive } : p
        );
        
        // Update the state
        setAllParents(updatedParents);
        
        toast.dismiss(loadingToastId);
        toast.success(
          isActive 
            ? `${parent.first_name} ${parent.last_name} activated` 
            : `${parent.first_name} ${parent.last_name} deactivated`
        );
      } else {
        toast.dismiss(loadingToastId);
        toast.error(`Failed to ${isActive ? 'activate' : 'deactivate'} parent`);
      }
    } catch (error) {
      console.error(`Error toggling parent active status:`, error);
      toast.dismiss(loadingToastId);
      toast.error(`Failed to ${isActive ? 'activate' : 'deactivate'} parent`);
    }
  };

  return {
    editingParent,
    setEditingParent,
    isEditParentDialogOpen,
    setIsEditParentDialogOpen,
    isDeletingParent,
    handleSendInvite,
    handleEditParent,
    handleParentUpdated,
    handleDeleteParent,
    handleToggleParentActiveStatus
  };
};
